import { definePreset } from '@primeuix/themes';
import Aura from '@primeuix/themes/aura';

export const secondaryPalette = {
  50: '#f0fdfa',
  100: '#ccfbf1',
  200: '#99f6e4',
  300: '#5eead4',
  400: '#2dd4bf',
  500: '#14b8a6',
  600: '#0d9488',
  700: '#0f766e',
  800: '#115e59',
  900: '#134e4a',
  950: '#042f2e',
} as const;

export const neutralPalette = {
  50: '#fafafa',
  100: '#f5f5f5',
  200: '#e5e5e5',
  300: '#d4d4d4',
  400: '#a3a3a3',
  500: '#737373',
  600: '#525252',
  700: '#404040',
  800: '#262626',
  900: '#171717',
  950: '#0a0a0a',
} as const;

const componentFocusRingNone = {
  width: '0',
  style: 'none',
  color: 'transparent',
  offset: '0',
  shadow: 'none',
} as const;

export const auraCustomPreset = definePreset(Aura, {
  semantic: {
    formField: {
      paddingX: '0.625rem',
      paddingY: '0.5rem',
      borderRadius: '8px',
    },
    focusRing: {
      width: '1px',
      style: 'solid',
      color: '{primary.color}',
      offset: '2px',
      shadow: 'none',
    },
    /** Глобальный primary: синяя шкала (в духе Tailwind blue). */
    primary: {
      50: '#eff6ff',
      100: '#dbeafe',
      200: '#bfdbfe',
      300: '#93c5fd',
      400: '#60a5fa',
      500: '#3b82f6',
      600: '#2563eb',
      700: '#1d4ed8',
      800: '#1e40af',
      900: '#1e3a8a',
      950: '#172554',
    },
    colorScheme: {
      light: {
        surface: {
          0: '#ffffff',
          50: '#f8fafc',
          100: '#f1f5f9',
          200: '#e2e8f0',
          300: '#cbd5e1',
          400: '#94a3b8',
          500: '#64748b',
          600: '#475569',
          700: '#334155',
          800: '#1e293b',
          900: '#334155',
          950: '#020617',
        },
        primary: {
          color: '{primary.500}',
          contrastColor: '{surface.0}',
          hoverColor: '{primary.600}',
          activeColor: '{primary.700}',
        },
        text: {
          color: '{surface.900}',
          hoverColor: '{surface.950}',
          mutedColor: '{surface.500}',
          hoverMutedColor: '{surface.600}',
        },
        content: {
          background: '{surface.0}',
          hoverBackground: '{surface.50}',
          borderColor: '{surface.200}',
          color: '{text.muted.color}',
          hoverColor: '{surface.900}',
        },
        overlay: {
          select: {
            background: '{content.background}',
            borderColor: '{content.border.color}',
            color: '{text.color}',
          },
          popover: {
            background: '{content.background}',
            borderColor: '{content.border.color}',
            color: '{text.color}',
          },
          modal: {
            background: '{content.background}',
            borderColor: '{content.border.color}',
            color: '{text.color}',
          },
        },
        formField: {
          background: '{surface.0}',
          disabledBackground: '{surface.50}',
          filledBackground: '{surface.0}',
          filledHoverBackground: '{surface.0}',
          filledFocusBackground: '{surface.0}',
          borderColor: '{surface.200}',
          hoverBorderColor: '{surface.400}',
          focusBorderColor: '{primary.color}',
          invalidBorderColor: '{red.600}',
          color: '{text.color}',
          disabledColor: '{surface.400}',
          placeholderColor: '{surface.500}',
          invalidPlaceholderColor: '{red.600}',
          floatLabelColor: '{surface.500}',
          floatLabelFocusColor: '{primary.color}',
          floatLabelActiveColor: '{text.muted.color}',
          floatLabelInvalidColor: '{red.600}',
          iconColor: '{surface.500}',
          shadow: 'none',
        },
      },
      dark: {
        surface: {
          0: '#09090b',
          50: '#18181b',
          100: '#27272a',
          200: '#3f3f46',
          300: '#52525b',
          400: '#71717a',
          500: '#a1a1aa',
          600: '#d4d4d8',
          700: '#e4e4e7',
          800: '#f4f4f5',
          900: '#fafafa',
          950: '#ffffff',
        },
        primary: {
          color: '{primary.400}',
          contrastColor: '{surface.950}',
          hoverColor: '{primary.300}',
          activeColor: '{primary.200}',
        },
        text: {
          color: '{surface.800}',
          hoverColor: '{surface.950}',
          mutedColor: '{surface.500}',
          hoverMutedColor: '{surface.700}',
        },
        content: {
          background: '{surface.50}',
          hoverBackground: '{surface.100}',
          borderColor: '{surface.200}',
          color: '{text.muted.color}',
          hoverColor: '{surface.950}',
        },
        overlay: {
          select: {
            background: '{content.background}',
            borderColor: '{content.border.color}',
            color: '{text.color}',
          },
          popover: {
            background: '{content.background}',
            borderColor: '{content.border.color}',
            color: '{text.color}',
          },
          modal: {
            background: '{content.background}',
            borderColor: '{content.border.color}',
            color: '{text.color}',
          },
        },
        formField: {
          background: '{surface.0}',
          disabledBackground: '{surface.200}',
          filledBackground: '{surface.0}',
          filledHoverBackground: '{surface.0}',
          filledFocusBackground: '{surface.0}',
          borderColor: '{surface.200}',
          hoverBorderColor: '{surface.300}',
          focusBorderColor: '{primary.color}',
          invalidBorderColor: '{red.400}',
          color: '{text.muted.color}',
          disabledColor: '{surface.400}',
          placeholderColor: '{surface.400}',
          invalidPlaceholderColor: '{red.300}',
          floatLabelColor: '{surface.500}',
          floatLabelFocusColor: '{primary.color}',
          floatLabelActiveColor: '{surface.600}',
          floatLabelInvalidColor: '{red.300}',
          iconColor: '{surface.500}',
          shadow: 'none',
        },
      },
    },
  },
  components: {
    datatable: {
      header: {
        background: '{content.background}',
        padding: '1rem',
      },
      headerCell: {
        background: '{content.background}',
        selectedBackground: '{primary.50}',
        selectedColor: '{primary.800}',
        padding: '0.875rem 1rem',
        focusRing: componentFocusRingNone,
      },
      row: {
        background: '{content.background}',
        color: '{text.color}',
        selectedBackground: '{primary.50}',
        selectedColor: '{primary.900}',
        stripedBackground: '{surface.50}',
        focusRing: componentFocusRingNone,
      },
      bodyCell: {
        padding: '0.875rem 1rem',
        selectedBorderColor: '{primary.200}',
      },
      footer: {
        background: '{content.background}',
        padding: '0.875rem 1rem',
      },
      footerCell: {
        background: '{content.background}',
        padding: '0.875rem 1rem',
      },
      sortIcon: {
        color: '{surface.500}',
      },
      colorScheme: {
        light: {
          root: {
            borderColor: '#eef2ff',
          },
          row: {
            color: '{surface.500}',
          },
        },
        dark: {
          root: {
            borderColor: '{content.border.color}',
          },
          headerCell: {
            selectedBackground: '{primary.900}',
            selectedColor: '{primary.100}',
          },
          row: {
            selectedBackground: '{primary.900}',
            selectedColor: '{surface.950}',
            stripedBackground: 'color-mix(in srgb, {surface.50} 55%, {surface.100})',
          },
          bodyCell: {
            selectedBorderColor: '{primary.500}',
          },
        },
      },
    },
    tag: {
      root: {
        padding: '4px 8px',
      },
      colorScheme: {
        light: {
          warn: { background: '#f59e0b', color: '#ffffff' },
          danger: { background: '#d83333', color: '#ffffff' },
          success: { background: '#229722', color: '#ffffff' },
        },
        dark: {
          warn: { background: '#f59e0b', color: '#ffffff' },
          danger: { background: '#d83333', color: '#ffffff' },
          success: { background: '#229722', color: '#ffffff' },
        },
      },
    },
    datepicker: {
      panel: {
        borderRadius: '8px',
      },
      colorScheme: {
        light: {
          dropdown: {
            background: '{surface.200}',
            hoverBackground: '{surface.300}',
            activeBackground: '{surface.400}',
            color: '{surface.600}',
            hoverColor: '{surface.700}',
            activeColor: '{surface.800}',
          },
        },
        dark: {
          dropdown: {
            background: '{surface.200}',
            hoverBackground: '{surface.300}',
            activeBackground: '{surface.400}',
            color: '{surface.600}',
            hoverColor: '{surface.700}',
            activeColor: '{surface.800}',
          },
        },
      },
    },
    menu: {
      item: {
        borderRadius: '0.5rem',
        padding: '0.625rem 0.875rem',
        gap: '0.625rem',
      },
      separator: {
        borderColor: '{content.border.color}',
      },
      colorScheme: {
        light: {
          root: {
            shadow: '0 10px 30px rgba(15, 23, 42, 0.12)',
            background: '{surface.0}',
            borderColor: '{surface.200}',
            color: '{surface.800}',
          },
          item: {
            color: '{surface.800}',
            focusBackground: '{content.hover.background}',
            focusColor: '{surface.800}',
            icon: {
              color: '{surface.800}',
              focusColor: '{surface.800}',
            },
          },
          submenuLabel: {
            color: '{surface.800}',
          },
        },
        dark: {
          root: {
            shadow: 'none',
          },
        },
      },
    },
    paginator: {
      root: {
        padding: '0.875rem 1rem',
        gap: '0.25rem',
        borderRadius: '0',
        color: '{text.muted.color}',
      },
      navButton: {
        hoverBackground: '{surface.100}',
        selectedBackground: '{primary.50}',
        color: '{text.muted.color}',
        hoverColor: '{surface.900}',
        selectedColor: '{primary.800}',
        width: '2.25rem',
        height: '2.25rem',
        borderRadius: '9999px',
        focusRing: componentFocusRingNone,
      },
      currentPageReport: {
        color: '{surface.500}',
      },
      colorScheme: {
        dark: {
          navButton: {
            selectedBackground: '{primary.900}',
            hoverColor: '{surface.950}',
            selectedColor: '{primary.100}',
          },
        },
      },
    },
  },
});
